/**
 * Wizard step `rules` (§16.1 step 5, §5.3 Entry / Eligibility / Schedule / Outcome): entry mode and price, caps,
 * eligibility, schedule with timezone, the outcome block per mechanic (tiers / pool / number range / milestones),
 * odds disclosure and redraw policy. Binds the config paths through react-hook-form so every validation issue
 * links to its control (`fieldId(path)`); autosave and validation belong to the frame.
 */
import { CountryCode, PrizeTier, type OutcomeRules } from '@windrop/contracts';
import { Button, useIntl } from '@windrop/ui';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  FormErrorSummary,
  RhfCheckbox,
  RhfSelect,
  RhfText,
  RhfTextarea,
  fieldId,
} from '../../../components/FormField';
import { useWizard } from './WizardFrame';
import { COMMON_TIMEZONES, stepForPath, type DraftConfigInput } from './steps';

const opt = <T extends string>(values: readonly T[], labels?: Partial<Record<T, string>>) =>
  values.map((v) => ({
    value: v,
    label: labels?.[v] ?? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' '),
  }));

const num = {
  parse: (raw: string) => (raw === '' ? undefined : Number(raw)),
  format: (v: unknown) => (v == null ? '' : String(v)),
};
/** Digits only, no leading zeros (MinorAmount is a decimal string, D01). */
const minor = (raw: string): string => raw.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '') || '0';
const intReq = { parse: (raw: string) => Number(raw), format: (v: unknown) => String(v ?? 0) };

/** `datetime-local` ↔ UTC ISO (D01: schedules stored in UTC, displayed in the lottery timezone). */
const toLocalInput = (iso: unknown): string => {
  if (typeof iso !== 'string' || !iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (raw: string): string | undefined => {
  if (!raw) return undefined;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw : d.toISOString();
};

export function RulesStep() {
  const { control } = useFormContext<DraftConfigInput>();
  const { issues, goToStep } = useWizard();
  const intl = useIntl();
  const entry = useWatch({ control, name: 'entry' });
  const outcome = useWatch({ control, name: 'outcome' }) as OutcomeRules | undefined;
  const prizes = useWatch({ control, name: 'prizes.prizes' }) ?? [];
  const identity = useWatch({ control, name: 'identity' });
  const schedule = useWatch({ control, name: 'schedule' });
  const mine = issues.filter((i) => i.step === 'rules');
  const prizeOptions = prizes.map((p) => ({
    value: p?.prizeId ?? '',
    label: `${p?.name?.en ?? p?.prizeId ?? 'prize'} (${p?.tier ?? '?'}, ×${p?.quantity ?? '?'})`,
  }));
  const paid = entry?.mode === 'PAID';
  const mechanic = outcome?.mechanic;
  const base = mechanic === 'FREE_GIVEAWAY' ? 'outcome.baseMechanic' : 'outcome';
  const block = mechanic === 'FREE_GIVEAWAY' ? outcome?.baseMechanic : outcome;
  const blockMechanic = block?.mechanic;
  const currency = identity?.currency ?? 'XTS';
  const tz = identity?.timezone ?? 'UTC';
  const scheduled = blockMechanic === 'SCHEDULED_RAFFLE' || blockMechanic === 'NUMBER_PICK';

  return (
    <div className="wd-stack" data-testid="rules-step">
      <FormErrorSummary
        issues={mine}
        title={`${mine.length} issue${mine.length === 1 ? '' : 's'} in rules`}
        onNavigate={(p) => goToStep(stepForPath(p))}
      />

      <h2>Entry (§5.3)</h2>
      <div className="wdc-form-grid">
        <RhfSelect
          control={control}
          name="entry.mode"
          label="Entry mode"
          required
          options={[
            { value: 'FREE', label: 'Free' },
            { value: 'PAID', label: 'Paid (MockProvider until the client names a provider, D03)' },
          ]}
          hint={
            mechanic === 'FREE_GIVEAWAY'
              ? 'A free giveaway is always free at zero price.'
              : 'Per-lottery setting from day one.'
          }
          disabled={mechanic === 'FREE_GIVEAWAY'}
        />
        <RhfText
          control={control}
          name="entry.priceMinor"
          label={`Price in minor units (${currency})`}
          required={paid}
          hint={
            paid
              ? `${entry?.priceMinor ? intl.formatMoney(String(entry.priceMinor), currency) : '—'} per entry; the paid CTA states the exact amount.`
              : 'Must be 0 for free entry.'
          }
          disabled={!paid}
          parse={minor}
        />
        <RhfSelect
          control={control}
          name="entry.requiredContact"
          label="Required contact"
          required
          options={[
            { value: 'EITHER', label: 'WhatsApp number or social handle' },
            { value: 'WHATSAPP', label: 'WhatsApp number' },
            { value: 'SOCIAL_HANDLE', label: 'Social handle' },
          ]}
        />
        <RhfText
          control={control}
          name="entry.eligibleQuantity.min"
          label="Entries per submission (min)"
          type="number"
          required
          {...intReq}
        />
        <RhfText
          control={control}
          name="entry.eligibleQuantity.max"
          label="Entries per submission (max)"
          type="number"
          required
          {...intReq}
        />
        <RhfText
          control={control}
          name="entry.caps.perPerson"
          label="Cap per person"
          type="number"
          required
          hint="Bonus entries excluded."
          {...intReq}
        />
        <RhfText control={control} name="entry.caps.perDay" label="Cap per day" type="number" {...num} />
        <RhfText
          control={control}
          name="entry.caps.global"
          label="Global cap"
          type="number"
          hint="For fixed pools: the pool sells out at this count."
          {...num}
        />
        <RhfText
          control={control}
          name="entry.bonusEntryCap"
          label="Bonus-entry cap (D07, max 10)"
          type="number"
          required
          {...intReq}
        />
        <RhfSelect
          control={control}
          name="entry.cancellationPolicy"
          label="Cancellation"
          options={opt(['NO_CANCELLATION', 'CANCEL_BEFORE_CLOSE', 'CANCEL_WITHIN_WINDOW'] as const)}
        />
        <RhfSelect
          control={control}
          name="entry.refundRule"
          label="Refund rule"
          options={opt(['FULL', 'FULL_MINUS_PROVIDER_FEES', 'NONE'] as const)}
        />
      </div>

      <h2>Eligibility</h2>
      <div className="wdc-form-grid">
        <RhfText
          control={control}
          name="eligibility.minAge"
          label="Minimum age"
          type="number"
          required
          hint="18+ default; below needs the market policy."
          {...intReq}
        />
        <RhfText
          control={control}
          name="eligibility.geography.allow"
          label="Allowed countries"
          hint="ISO-3166 alpha-2, comma separated; empty = any country. Drives the unavailable-by-market state."
          format={(v) => (Array.isArray(v) ? v.join(', ') : '')}
          parse={(raw) =>
            raw
              .split(',')
              .map((s) => s.trim().toUpperCase())
              .filter((s) => CountryCode.safeParse(s).success)
          }
        />
        <RhfText
          control={control}
          name="eligibility.geography.deny"
          label="Denied countries"
          format={(v) => (Array.isArray(v) ? v.join(', ') : '')}
          parse={(raw) =>
            raw
              .split(',')
              .map((s) => s.trim().toUpperCase())
              .filter((s) => CountryCode.safeParse(s).success)
          }
        />
        <RhfText
          control={control}
          name="eligibility.exclusions"
          label="Exclusions"
          hint="Semicolon separated; shown in the rules, e.g. previous grand-prize winners."
          format={(v) => (Array.isArray(v) ? v.join('; ') : '')}
          parse={(raw) =>
            raw
              .split(';')
              .map((s) => s.trim())
              .filter(Boolean)
          }
        />
        <RhfCheckbox control={control} name="eligibility.residencyRequired" label="Residency required" />
        <RhfCheckbox
          control={control}
          name="eligibility.employeesExcluded"
          label="Organizer employees excluded"
        />
        <RhfCheckbox
          control={control}
          name="eligibility.sponsorEmployeesExcluded"
          label="Sponsor employees excluded"
        />
      </div>

      <h2>Schedule (D01: UTC storage, displayed in {tz})</h2>
      <div className="wdc-form-grid">
        <RhfSelect
          control={control}
          name="identity.timezone"
          label="Timezone"
          required
          options={[...new Set([tz, ...COMMON_TIMEZONES])].map((z) => ({ value: z, label: z }))}
          hint="IANA zone used for every displayed time."
        />
        <RhfText
          control={control}
          name="schedule.opensAt"
          label="Opens"
          type="datetime-local"
          required
          format={toLocalInput}
          parse={fromLocalInput}
        />
        <RhfText
          control={control}
          name="schedule.closesAt"
          label="Closes"
          type="datetime-local"
          required
          format={toLocalInput}
          parse={fromLocalInput}
        />
        <RhfText
          control={control}
          name="schedule.drawAt"
          label="Draw"
          type="datetime-local"
          required={scheduled}
          hint={
            scheduled
              ? 'Required for scheduled mechanics; never before close.'
              : 'Instant mechanics decide at confirmed entry.'
          }
          format={toLocalInput}
          parse={fromLocalInput}
        />
        <RhfText
          control={control}
          name="schedule.resultsPublishAt"
          label="Results publication"
          type="datetime-local"
          hint="Defaults to the draw time."
          format={toLocalInput}
          parse={fromLocalInput}
        />
        <RhfSelect
          control={control}
          name="schedule.recurrence"
          label="Recurrence"
          options={opt(['NONE', 'DAILY', 'WEEKLY', 'MONTHLY'] as const)}
        />
        <RhfSelect
          control={control}
          name="schedule.latePaymentBehaviour"
          label="Late payment"
          options={[{ value: 'EXCLUDE_AND_REFUND', label: 'Exclude and refund (§10.4)' }]}
        />
      </div>
      {schedule?.opensAt && schedule?.closesAt ? (
        <p className="wd-muted">
          Opens {intl.formatDateTime(String(schedule.opensAt))} · closes{' '}
          {intl.formatDateTime(String(schedule.closesAt))}
          {schedule.drawAt ? ` · draw ${intl.formatDateTime(String(schedule.drawAt))}` : ''}
        </p>
      ) : null}

      <h2>Outcome — {blockMechanic ? blockMechanic.toLowerCase().replace(/_/g, ' ') : 'mechanic'}</h2>
      <p className="wd-muted">The mechanic itself is chosen on the Template step; its rules live here.</p>
      {blockMechanic === 'SCHEDULED_RAFFLE' ? <RaffleTiers base={base} prizeOptions={prizeOptions} /> : null}
      {blockMechanic === 'INSTANT_WIN' ||
      blockMechanic === 'SPIN_WHEEL' ||
      blockMechanic === 'SCRATCH_CARD' ? (
        <FixedPool base={base} prizeOptions={prizeOptions} wheel={blockMechanic === 'SPIN_WHEEL'} />
      ) : null}
      {blockMechanic === 'NUMBER_PICK' ? (
        <NumberPick base={base} prizeOptions={prizeOptions} currency={currency} />
      ) : null}
      {blockMechanic === 'FIRST_N' ? <FirstN base={base} prizeOptions={prizeOptions} /> : null}

      <h2>Odds disclosure (§4.2 A)</h2>
      <div className="wdc-form-grid">
        <RhfSelect
          control={control}
          name="outcome.oddsDisclosure.basis"
          label="Basis"
          required
          options={[
            { value: 'W_OF_N', label: 'W winning tickets of N (fixed pool)' },
            { value: 'ONE_IN_N', label: '1 in N (number pick)' },
            { value: 'DEPENDS_ON_ENTRIES', label: 'Depends on entries (raffle)' },
            { value: 'FIXED_POSITIONS', label: 'Fixed positions (first-N)' },
          ]}
        />
        <RhfTextarea
          control={control}
          name="outcome.oddsDisclosure.statement.en"
          label="Plain-language statement (en)"
          required
          rows={2}
          hint="Shown next to the primary CTA. Never implies skill or influence."
        />
        <RhfText
          control={control}
          name="outcome.oddsDisclosure.winningTickets"
          label="Winning tickets (W)"
          type="number"
          hint="W/N must equal the pool vector."
          {...num}
        />
        <RhfText
          control={control}
          name="outcome.oddsDisclosure.poolSize"
          label="Pool size (N)"
          type="number"
          {...num}
        />
      </div>

      <h2>Redraw and unclaimed prizes (§2.4, §9.4)</h2>
      <div className="wdc-form-grid">
        <RhfText
          control={control}
          name="outcome.redrawPolicy.maxRedraws"
          label="Maximum audited redraws"
          type="number"
          required
          {...intReq}
        />
        <RhfText
          control={control}
          name="outcome.redrawPolicy.dualOperatorAboveMinor"
          label="Second operator above (minor units)"
          hint="High-value reveals need two operators (§9.5)."
          parse={(raw) => (raw.replace(/[^0-9]/g, '') ? minor(raw) : undefined)}
        />
        <RhfSelect
          control={control}
          name="claims.unclaimedPrizePolicy"
          label="Unclaimed-prize policy"
          required
          options={opt(['REDRAW', 'ROLLOVER', 'FORFEIT'] as const)}
          hint="One audited redraw after the claim window by default; never an improvised admin action."
        />
        <p className="wd-muted" id={fieldId('outcome.algorithmVersion')}>
          Verification algorithm: <code>{outcome?.algorithmVersion ?? 'DLP1-HMAC-SHA256-FY'}</code> (fixed,
          §9).
        </p>
      </div>
    </div>
  );
}

type PrizeOption = { value: string; label: string };

function RaffleTiers({ base, prizeOptions }: { base: string; prizeOptions: PrizeOption[] }) {
  const { control } = useFormContext<DraftConfigInput>();
  const name = `${base}.scheduledRaffle.tiers` as 'outcome.scheduledRaffle.tiers';
  const tiers = useFieldArray({ control, name });
  return (
    <div className="wd-stack" data-testid="outcome-raffle">
      <p className="wd-muted">
        Fixed tier order: shuffled entries are mapped to tiers in this order (§9.1 step 5).
      </p>
      {tiers.fields.map((f, i) => (
        <fieldset key={f.id} className="wdc-form-grid wdc-fieldset">
          <RhfSelect
            control={control}
            name={`${name}.${i}.tier`}
            label="Tier"
            required
            options={opt(PrizeTier.options)}
          />
          <RhfSelect
            control={control}
            name={`${name}.${i}.prizeId`}
            label="Prize"
            required
            options={prizeOptions}
          />
          <RhfText
            control={control}
            name={`${name}.${i}.winners`}
            label="Winners"
            type="number"
            required
            {...intReq}
          />
          <Button variant="quiet" onClick={() => tiers.remove(i)}>
            Remove tier
          </Button>
        </fieldset>
      ))}
      <Button
        variant="secondary"
        onClick={() => tiers.append({ tier: 'MINOR', prizeId: prizeOptions[0]?.value ?? '', winners: 1 })}
      >
        Add tier
      </Button>
      <RhfCheckbox
        control={control}
        name={
          `${base}.scheduledRaffle.allowMultipleTierWins` as 'outcome.scheduledRaffle.allowMultipleTierWins'
        }
        label="An entry may win more than one tier"
      />
    </div>
  );
}

function FixedPool({
  base,
  prizeOptions,
  wheel,
}: {
  base: string;
  prizeOptions: PrizeOption[];
  wheel: boolean;
}) {
  const { control } = useFormContext<DraftConfigInput>();
  const name = `${base}.fixedPool.outcomes` as 'outcome.fixedPool.outcomes';
  const outcomes = useFieldArray({ control, name });
  const segName = `${base}.fixedPool.segments` as 'outcome.fixedPool.segments';
  const segments = useFieldArray({ control, name: segName });
  return (
    <div className="wd-stack" data-testid="outcome-pool">
      <div className="wdc-form-grid">
        <RhfText
          control={control}
          name={`${base}.fixedPool.poolSize` as 'outcome.fixedPool.poolSize'}
          label="Pool size (N tickets)"
          type="number"
          required
          hint="Committed and hashed before entries open (§9.2)."
          {...intReq}
        />
        <RhfSelect
          control={control}
          name={`${base}.fixedPool.exhaustionBehaviour` as 'outcome.fixedPool.exhaustionBehaviour'}
          label="When stock runs out"
          options={[
            { value: 'PAUSE_NEW_OUTCOMES', label: 'Pause new outcomes (fail closed)' },
            { value: 'CLOSE_POOL', label: 'Close the pool' },
          ]}
        />
      </div>
      <p className="wd-muted">Winning tickets per prize; losses fill the rest of the pool.</p>
      {outcomes.fields.map((f, i) => (
        <fieldset key={f.id} className="wdc-form-grid wdc-fieldset">
          <RhfSelect
            control={control}
            name={`${name}.${i}.prizeId`}
            label="Prize"
            required
            options={prizeOptions}
          />
          <RhfText
            control={control}
            name={`${name}.${i}.count`}
            label="Winning tickets"
            type="number"
            required
            {...intReq}
          />
          <Button variant="quiet" onClick={() => outcomes.remove(i)}>
            Remove
          </Button>
        </fieldset>
      ))}
      <Button
        variant="secondary"
        onClick={() => outcomes.append({ prizeId: prizeOptions[0]?.value ?? '', count: 1 })}
      >
        Add winning outcome
      </Button>
      {wheel ? (
        <>
          <p className="wd-muted">
            Wheel segments are presentation only — never a live probability indicator (§5.1).
          </p>
          {segments.fields.map((f, i) => (
            <fieldset key={f.id} className="wdc-form-grid wdc-fieldset">
              <RhfText control={control} name={`${segName}.${i}.label`} label="Segment label" required />
              <RhfSelect
                control={control}
                name={`${segName}.${i}.prizeId`}
                label="Prize (empty = loss)"
                options={[{ value: '', label: 'Thanks for playing' }, ...prizeOptions]}
              />
              <Button variant="quiet" onClick={() => segments.remove(i)}>
                Remove segment
              </Button>
            </fieldset>
          ))}
          <Button
            variant="secondary"
            onClick={() => segments.append({ label: 'Thanks for playing', prizeId: null })}
          >
            Add segment
          </Button>
        </>
      ) : null}
    </div>
  );
}

function NumberPick({
  base,
  prizeOptions,
  currency,
}: {
  base: string;
  prizeOptions: PrizeOption[];
  currency: string;
}) {
  const { control } = useFormContext<DraftConfigInput>();
  const name = `${base}.numberPick.matchTiers` as 'outcome.numberPick.matchTiers';
  const tiers = useFieldArray({ control, name });
  return (
    <div className="wd-stack" data-testid="outcome-number-pick">
      <div className="wdc-form-grid">
        <RhfText
          control={control}
          name={`${base}.numberPick.range.min` as 'outcome.numberPick.range.min'}
          label="Lowest number"
          type="number"
          required
          {...intReq}
        />
        <RhfText
          control={control}
          name={`${base}.numberPick.range.max` as 'outcome.numberPick.range.max'}
          label="Highest number"
          type="number"
          required
          {...intReq}
        />
        <RhfText
          control={control}
          name={`${base}.numberPick.pickCount` as 'outcome.numberPick.pickCount'}
          label="Numbers to pick"
          type="number"
          required
          {...intReq}
        />
        <RhfSelect
          control={control}
          name={`${base}.numberPick.quickPick` as 'outcome.numberPick.quickPick'}
          label="Quick Pick"
          options={opt(['ALLOWED', 'REQUIRED', 'DISABLED'] as const)}
        />
        <RhfText
          control={control}
          name={`${base}.numberPick.maxLiabilityMinor` as 'outcome.numberPick.maxLiabilityMinor'}
          label={`Published maximum liability (minor ${currency})`}
          required
          hint="Must cover the match table × prize units."
          parse={minor}
        />
        <RhfSelect
          control={control}
          name={`${base}.numberPick.multipleWinnersPolicy` as 'outcome.numberPick.multipleWinnersPolicy'}
          label="Several winners in one tier"
          options={[
            { value: 'EACH_FIXED_UNIT', label: 'Each wins a fixed unit while units last' },
            { value: 'SHARE_TIER_UNITS', label: 'Share the tier units' },
          ]}
        />
      </div>
      <p className="wd-muted">Match tiers: exact matches → prize and fixed units (§9.3).</p>
      {tiers.fields.map((f, i) => (
        <fieldset key={f.id} className="wdc-form-grid wdc-fieldset">
          <RhfText
            control={control}
            name={`${name}.${i}.matches`}
            label="Matches"
            type="number"
            required
            {...intReq}
          />
          <RhfSelect
            control={control}
            name={`${name}.${i}.prizeId`}
            label="Prize"
            required
            options={prizeOptions}
          />
          <RhfText
            control={control}
            name={`${name}.${i}.prizeUnits`}
            label="Prize units"
            type="number"
            required
            {...intReq}
          />
          <Button variant="quiet" onClick={() => tiers.remove(i)}>
            Remove tier
          </Button>
        </fieldset>
      ))}
      <Button
        variant="secondary"
        onClick={() => tiers.append({ matches: 3, prizeId: prizeOptions[0]?.value ?? '', prizeUnits: 1 })}
      >
        Add match tier
      </Button>
    </div>
  );
}

function FirstN({ base, prizeOptions }: { base: string; prizeOptions: PrizeOption[] }) {
  const { control } = useFormContext<DraftConfigInput>();
  const name = `${base}.firstN.milestones` as 'outcome.firstN.milestones';
  const milestones = useFieldArray({ control, name });
  return (
    <div className="wd-stack" data-testid="outcome-first-n">
      <div className="wdc-form-grid">
        <RhfSelect
          control={control}
          name={`${base}.firstN.countingMode` as 'outcome.firstN.countingMode'}
          label="Counting mode"
          required
          options={[
            { value: 'UNIQUE_PARTICIPANT', label: 'Unique participants (first qualified entry)' },
            { value: 'QUALIFIED_ENTRY', label: 'Qualified entries' },
          ]}
          hint="Stable and published; refunds never renumber history."
        />
        <RhfText
          control={control}
          name={`${base}.firstN.everyNth.n` as 'outcome.firstN.everyNth.n'}
          label="Every Nth position wins"
          type="number"
          {...num}
        />
        <RhfSelect
          control={control}
          name={`${base}.firstN.everyNth.prizeId` as 'outcome.firstN.everyNth.prizeId'}
          label="Every-Nth prize"
          options={[{ value: '', label: '—' }, ...prizeOptions]}
        />
        <RhfText
          control={control}
          name={`${base}.firstN.everyNth.limit` as 'outcome.firstN.everyNth.limit'}
          label="Every-Nth winners limit"
          type="number"
          {...num}
        />
      </div>
      <p className="wd-muted">Explicit milestone positions win first.</p>
      {milestones.fields.map((f, i) => (
        <fieldset key={f.id} className="wdc-form-grid wdc-fieldset">
          <RhfText
            control={control}
            name={`${name}.${i}.position`}
            label="Position"
            type="number"
            required
            {...intReq}
          />
          <RhfSelect
            control={control}
            name={`${name}.${i}.prizeId`}
            label="Prize"
            required
            options={prizeOptions}
          />
          <Button variant="quiet" onClick={() => milestones.remove(i)}>
            Remove milestone
          </Button>
        </fieldset>
      ))}
      <Button
        variant="secondary"
        onClick={() => milestones.append({ position: 1, prizeId: prizeOptions[0]?.value ?? '' })}
      >
        Add milestone
      </Button>
    </div>
  );
}
